import { WidgetSettings } from '@gsc/types'
import { FunctionConfig } from '@gsc/utils'

import { parseQuery } from '../shared/url'
import { IWidget } from '../interfaces'
import { filterABTest as performABTest } from './targeting/ab-filtering'
import { Targeting } from './targeting/targeting'
import { Event, EventType } from './tracking'
import { WidgetRuntime } from './runtime'

// widgets display management
//
export class WidgetDisplay {
  private _created: { [key: number]: IWidget } = {}
  private targeting: Targeting
  private widgets: WidgetSettings[]

  constructor(private runtime: WidgetRuntime) {}

  private async initialize(widgets?: WidgetSettings[]) {
    if (!this.runtime.renderer.isRendered()) {
      this.runtime.renderer.render(this.runtime.args.getParams())
    }

    if (!widgets) {
      return
    }

    console.log('initialize')
    if (this.widgets) {
      console.warn('widgets already initialized')
      return
    }

    // initialize
    this.widgets = widgets.sort((a, b) => (b.sort_order || 0) - (a.sort_order || 0))

    if (!this.widgets.length) {
      return
    }

    // initialize targeting
    if (!this.targeting) {
      this.targeting = new Targeting(this.runtime)
      this.targeting.initialize(this.widgets)
      this.targeting.on('change', async () => {
        await this.update()
      })
    }

    // initialize platform
    if (this.runtime.platform) {
      console.log('wait platform init')
      await this.runtime.platform.initialize(this.widgets, this.targeting)
    }

    // prepare widgets
    // todo: detect widgets to show and preload images
  }

  public async update(widgets?: WidgetSettings[]) {
    await this.initialize(widgets)

    if (!this.widgets) {
      throw new Error('not initialized')
    }

    if (this.widgets.length === 0) {
      return
    }

    // update context (preload dtr)
    await this.updateContext()
    await this.displayWidgets()
  }

  public async displayWidgets(): Promise<void> {
    const abTestResult = performABTest(this.widgets, this.runtime).map(w => w.id)
    const testSpecial = (widgetId: number): boolean | undefined => {
      // if closed by user - do not show again
      const userDisplayAction = this.getWidgetUserDisplayAction(widgetId)
      if (userDisplayAction === false) {
        console.log(`widget ${widgetId} was closed by user`)
        return false
      }
      // if lose ab-test - close, but only if it wasn't shown by user
      if (abTestResult.indexOf(widgetId) === -1 && userDisplayAction !== true) {
        console.log(`widget ${widgetId} ab test failed`)
        return false
      }
    }

    const targeting = await this.targeting.getState(new Date())

    this.widgets.forEach(widget => {
      const displayed = this.displayAt(widget.id)

      let decision = testSpecial(widget.id)
      if (decision == null) {
        decision = targeting.test(widget.id, displayed)
      }

      if (!displayed && decision) {
        this._createWidget(widget)
      } else if (displayed && !decision) {
        this._hideWidget(widget)
      }
    })
  }

  public findWidget(id: number): WidgetSettings | undefined {
    return this.widgets.find(w => w.id === id)
  }

  public async showWidgetUser(id: number): Promise<boolean> {
    await this.initialize()
    const widget = this.findWidget(id)
    if (widget) {
      const res = await this._createWidget(widget)
      if (res) {
        this.widgetShownByUser(id)
      }
      return true
    }
    return false
  }

  public async closeWidgetUser(id: number): Promise<boolean> {
    await this.initialize()
    const widget = this.findWidget(id)
    if (widget) {
      const res = await this._destroyWidget(widget)
      if (res) {
        this.runtime.track.add(new Event(EventType.Close, widget))
        this.widgetClosedByUser(id)
      }
      return true
    }
    return false
  }

  public async hideWidget(id: number): Promise<boolean> {
    await this.initialize()
    const widget = this.findWidget(id)
    if (widget) {
      return await this._hideWidget(widget)
    }
    return false
  }

  public async updateWidgetTags(ctx: { [key: string]: any }) {
    await this.initialize()
    this.runtime.renderer.updateContext(ctx)
  }

  public async _destroyWidget(widget: WidgetSettings): Promise<boolean> {
    const instance = this._created[widget.id]
    if (instance && instance.shown) {
      instance.close()
      delete this._created[widget.id]
      return true
    }
    return false
  }

  public async _hideWidget(widget: WidgetSettings): Promise<boolean> {
    if (await this._destroyWidget(widget)) {
      this.runtime.track.add(new Event(EventType.Hide, widget))
    }
    return true
  }

  public async _createWidget(widget: WidgetSettings): Promise<boolean> {
    if (this._created[widget.id]) {
      return false
    }
    this._created[widget.id] = {
      shown: false,
    } as IWidget // lock
    this._created[widget.id] = await this.runtime.renderer.createWidget(widget)

    if (!this._created[widget.id].shown) {
      delete this._created[widget.id]
      return false
    }

    this.runtime.track.add(new Event(EventType.Show, widget))
    return true
  }

  // store user action in page context
  public widgetClosedByUser(id: number): void {
    this.runtime.storages.page.set(`byUser.${id}`, false)
  }

  public widgetShownByUser(id: number): void {
    this.runtime.storages.page.set(`byUser.${id}`, true)
  }

  public getWidgetUserDisplayAction(id: number): boolean | undefined {
    return this.runtime.storages.page.get(`byUser.${id}`)
  }

  public async pageView() {
    if (this.targeting) {
      this.targeting.reset()
    }

    this.updateContext()
  }

  private displayAt(widgetId: number): Date | undefined {
    return (this._created[widgetId] || {}).date
  }

  private async updateContext() {
    await this.initialize()

    const date = new Date()
    const serverData = await this.runtime.session.getServerData()
    const lang = navigator.language
    const visit = this.runtime.session.currentVisit
    const { href, search, pathname, hash } = this.runtime.location()
    const query = parseQuery(search)

    const tags: { [key: string]: any } = {
      $date: date.toLocaleDateString(lang),
      $month: date.toLocaleString(lang, { month: 'long' }),
      $weekday: date.toLocaleString(lang, { weekday: 'long' }),
      $day: date.toLocaleString(lang, { day: 'numeric' }),

      $country: serverData.geo.country,
      $countryCode: serverData.geo.country_code,
      $region: serverData.geo.region,
      $state: serverData.geo.region,
      $stateCode: serverData.geo.state_code,
      $city: serverData.geo.city,
      $postalCode: serverData.geo.postal_code,

      $browser: serverData.ua.browser,
      $device: serverData.ua.device,
      $os: serverData.ua.os,
      $deviceType: serverData.ua.platform,

      $location: href,
      $locationPathname: pathname,
      $locationFullpath: pathname + search + hash,

      ...(visit
        ? {
            $language: visit.language,

            $utmMedium: visit.utm.utm_medium,
            $utmSource: visit.utm.utm_source,
            $utmCampaign: visit.utm.utm_campaign,
            $utmContent: visit.utm.utm_content,
            $utmTerm: visit.utm.utm_term,
          }
        : {}),

      // widget specific was added late at getTemplateTagsForWidget
      // $widget_id: id,
      // $widget_name: name,
    }

    // add all get query params
    for (const key in query) {
      tags['$' + key] = query[key]
    }

    // deprecated in snake case tags
    tags['$country_code'] = tags['$countryCode']
    tags['$state_code'] = tags['$stateCode']
    tags['$postal_code'] = tags['$postalCode']
    tags['$device_type'] = tags['$deviceType']
    tags['$utm_medium'] = tags['$utmMedium']
    tags['$utm_source'] = tags['$utmSource']
    tags['$utm_campaign'] = tags['$utmCampaign']
    tags['$utm_content'] = tags['$utmContent']
    tags['$utm_term'] = tags['$utmTerm']

    tags['$location_pathname'] = tags['$locationPathname']
    tags['$location_fullpath'] = tags['$locationFullpath']

    // add all platform tags
    if (this.runtime.platform) {
      const platformTags = await this.runtime.platform.getTags()
      for (const key in platformTags) {
        tags['$' + key] = platformTags[key]
      }
    }

    if (this.runtime.settings.currencyFormat) {
      tags[FunctionConfig.CurrencyFormat] = this.runtime.settings.currencyFormat
    }

    this.runtime.renderer.updateContext(tags)
  }
}
